/* ============================================================
   AEPERION — Motion core
   Shared choreography. Vanilla ES module, no framework — this file
   drops into a Shopify theme's assets/ unchanged.

   House rules:
   - Nothing overshoots. This brand does not bounce.
   - Every effect degrades to "visible, static" without JS.
   - Nothing in this file ever hides content. Not for a frame.

   WHAT IS LEFT IN HERE, AND WHY IT IS SO SMALL
   Everything scroll-driven has moved to CSS. Reveals, heading entrances
   and the stagger are `animation-timeline: view()` in base.css; the
   header show/hide below is a passive scroll listener. This module no
   longer creates a single ScrollTrigger of its own — and as of the GSAP
   removal at the foot of this file, it imports no animation library at
   all. The whole shared layer is now dependency-free.
   ============================================================ */


/* ---------- Ease. Singular. ----------
   Hand-drawn. Long flat tail: motion decelerates like mass coming to rest,
   never like a spring settling. This is the single most audible difference
   between a designed site and a templated one — and it only works if
   EVERYTHING uses it.

   This USED to be `CustomEase.create('glide', 'M0,0 C0.16,0 0.10,1 1,1')`,
   which is 3.6 kB gzip of GSAP plugin to evaluate one cubic segment. The
   path has exactly two control points, so it is expressible as an ordinary
   cubic-bezier — which is why tokens.css already carries the identical
   curve as `--e-glide: cubic-bezier(0.16, 0, 0.10, 1)`, control point for
   control point, with no approximation. The solver below is that same
   curve, and CustomEase is gone.

   If you ever change one, change both. They are the same curve expressed
   twice because two engines need to read it.

   The one exception lives in CSS and stays in CSS: --e-in-out, symmetric,
   for reversible state (the theme toggle). Nothing in JS is reversible. */

/**
 * Evaluate a CSS cubic-bezier timing function. Newton–Raphson with a
 * bisection fallback, which is what every browser does internally.
 * @returns {(x: number) => number} progress in, eased value out
 */
export function cubicBezier(x1, y1, x2, y2) {
  const A = (a, b) => 1 - 3 * b + 3 * a;
  const B = (a, b) => 3 * b - 6 * a;
  const C = (a) => 3 * a;
  const calc = (t, a, b) => ((A(a, b) * t + B(a, b)) * t + C(a)) * t;
  const slope = (t, a, b) => 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a);

  return (x) => {
    if (!(x > 0)) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const d = slope(t, x1, x2);
      if (!d) break;
      const err = calc(t, x1, x2) - x;
      if (Math.abs(err) < 1e-6) break;
      t -= err / d;
    }
    if (!(t >= 0 && t <= 1)) {
      let lo = 0, hi = 1;
      t = x;
      for (let i = 0; i < 24; i++) {
        const v = calc(t, x1, x2);
        if (Math.abs(v - x) < 1e-6) break;
        if (v > x) hi = t; else lo = t;
        t = (lo + hi) / 2;
      }
    }
    return calc(t, y1, y2);
  };
}

export const EASE = {
  /** Identical to tokens.css `--e-glide`. */
  glide: cubicBezier(0.16, 0, 0.10, 1),
};

/** The same curve as a string, for anything driven by WAAPI or CSS from JS. */
export const EASE_CSS = 'cubic-bezier(0.16, 0, 0.10, 1)';

export const reduced = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Programmatic scroll with a reality check. Chromium launched with smooth
   scrolling disabled — privacy forks ship this switch on — DROPS smooth
   programmatic scrolls instead of degrading them to instant: the call
   returns, nothing moves, no error. And because behavior:'auto' defers to
   CSS scroll-behavior, a smooth container swallows the "instant" spelling
   too. So: ask for the motion the reader deserves, then verify the scroller
   actually moved a beat later and force the instant path if it did not.
   The fallback re-issue is a no-op when the scroll already happened or the
   target was already in place. */
export function scrollIntoViewSafe(target, opts, scroller) {
  if (reduced()) {
    target.scrollIntoView({ ...opts, behavior: 'instant' });
    return;
  }
  const from = scroller ? { x: scroller.scrollLeft, y: scroller.scrollTop } : null;
  target.scrollIntoView({ ...opts, behavior: 'smooth' });
  if (!from) return;
  setTimeout(() => {
    if (scroller.scrollLeft === from.x && scroller.scrollTop === from.y) {
      target.scrollIntoView({ ...opts, behavior: 'instant' });
    }
  }, 150);
}

/* ============================================================
   Reveals and heading entrances — both live in CSS now.
   See the [data-reveal] / [data-split] block in base.css.
   ============================================================ */

/* `initReveals` was a GSAP/ScrollTrigger implementation and it flickered
   badly on mobile. Two causes, both real:

   1. `revealFailsafe` cleared each element's inline opacity after 2.6s to
      guarantee content could never stay hidden. But the ScrollTrigger tween
      was still armed, and gsap.fromTo re-applies its `from` vars on play —
      so the element became visible, then snapped back to opacity 0 the
      moment you scrolled to it, then faded in.
   2. Inverse-FOUC: `.js` was added by initMotion, after a 48.8 kB GSAP chunk
      parsed. Content painted, then `.js [data-reveal]{opacity:0}` landed and
      blanked it.

   `initHeadingSplits` then turned out to have the SAME disease in a
   different costume, and it was the one the client was actually seeing:

       gsap.from(el, { y: 26, opacity: 0, duration: 1,
                       immediateRender: false,
                       scrollTrigger: { trigger: el, start: 'top 88%' } })

   `immediateRender: false` defers the `from` state until the tween starts,
   and the tween starts at `top 88%` — 12% of a viewport AFTER the heading
   has been laid out and painted. Measured on a real scroll capture: every
   [data-split] heading on index, shimo and the product pages was visible at
   opacity 1 for 33–367 ms, then written to opacity 0, then faded back over
   a second. Visible → blank → fade, exactly as reported. The product pages
   have no [data-reveal] elements at all and still did it, which is what
   ruled out the CSS reveal system and lazy image decode as the cause.

   There is no ordering of GSAP calls that fixes this. A script cannot apply
   a hidden state before first paint; a render-blocking stylesheet can. The
   hidden state is therefore still CSS — see "Scroll reveals" in base.css —
   and this only decides WHEN it comes off.

   It went through one more iteration before landing here. The hidden state
   was lifted by a `steps(1)` scroll-driven animation flipping a custom
   property, with a CSS transition doing the easing. That never ran: a
   custom property written by an animation is animation-tainted, and a
   transition derived from a tainted variable does not start. Every reveal
   on the site was a hard cut, and `animation-fill-mode: both` on a view()
   timeline also un-flipped the flag on the way back up, so content blinked
   out while still on screen. An IntersectionObserver has neither problem:
   the class it adds is not tainted, so the transition eases, and it
   unobserves on the way in, so the reveal is one-way by construction. */

/**
 * Lift the reveal hidden state as each element comes into frame.
 *
 * @param {Document|Element} [scope=document]
 */
export function initReveals(scope = document) {
  const els = [...scope.querySelectorAll('[data-reveal], [data-split]')];
  if (!els.length) return;

  /* The stylesheet's hidden state is gated on this attribute, so removing
     it is also the failsafe: no observer, or motion is unwelcome, and the
     page is simply visible. */
  const giveUp = () => document.documentElement.removeAttribute('data-anim');

  if (reduced() || !('IntersectionObserver' in window)) { giveUp(); return; }

  /* Anything already in frame was never revealed — it was simply there.
     `is-instant` suppresses the transition for it, so the page does not
     play an entrance for content the reader is already looking at. Two
     rAFs, because the class has to survive one style resolution at the
     end state before the transition may be re-enabled. */
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const seeded = [];
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.top < vh && r.bottom > 0) {
      el.classList.add('is-in', 'is-instant');
      seeded.push(el);
    }
  }
  if (seeded.length) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      seeded.forEach((el) => el.classList.remove('is-instant'));
    }));
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        io.unobserve(e.target);                 // one-way: never un-reveals
        e.target.classList.add('is-in');
      }
    },
    // Hold the reveal back until the element is properly in frame rather
    // than clipped by the bottom edge. Negative bottom margin only.
    { rootMargin: '0px 0px -8% 0px' },
  );

  for (const el of els) if (!el.classList.contains('is-in')) io.observe(el);
}

export function initHeadingSplits() {}

/* ============================================================
   Header: hides on scroll-down, returns on scroll-up.

   Was a ScrollTrigger with an onUpdate. It is a passive scroll listener
   coalesced into one rAF instead: no library, no layout reads, no work
   at all on frames where the class does not change. `scrollY` is the
   only thing read, and reading it does not force layout.
   ============================================================ */
export function initHeader(header) {
  if (!header) return () => {};

  let last = window.scrollY || 0;
  let queued = false;

  /* classList.toggle(name, force) still mutates the attribute in some
     engines when the state is unchanged, which invalidates style on the
     fixed header on every single frame of a scroll. Compare first. */
  const set = (name, on) => {
    if (header.classList.contains(name) !== on) header.classList.toggle(name, on);
  };

  const apply = () => {
    queued = false;
    const y = window.scrollY || 0;
    set('is-stuck', y > 24);
    if (!reduced()) set('is-hidden', y > last && y > 220);
    last = y;
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(apply);
  };

  apply();
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => window.removeEventListener('scroll', onScroll);
}

/* ============================================================
   GSAP IS GONE.

   It used to be 87–88% of this site's JavaScript. The two-step plan that
   used to live here is done:

     1. home.js initMeters() was `gsap.from(pips, { scrollTrigger })`.
        The pips are already filled in the markup, so the tween was a
        0.5s scale-in that ALSO wrote opacity:0 onto twenty elements at
        import time — a spec readout hidden pending JS, which is the
        exact pattern this project is defined against. Now CSS, on the
        same animation-timeline mechanism as every other reveal.
     2. transition.js is WAAPI, for +0.3 kB gz.

   Also removed along the way: SplitText (imported, registered, never
   called), CustomEase (identical to --e-glide, which cubicBezier()
   above now expresses natively), and FrameSequence/initSequence, whose
   last caller disappeared when the Shimo hero became a <video>.

   Nothing in the shared layer imports GSAP any more, so five of six
   pages ship none of it.
   ============================================================ */

export function initMotion() {
  document.documentElement.classList.add('js');
  try {
    /* Same claim flag initShell() uses. On the static pages only one of the
       two ever binds the header; on the storefront the layout boots the
       shell before any page module runs, so without this check every page
       module would attach a second scroll listener toggling the same
       classes — harmless, but a listener per navigation adds up. */
    const el = document.querySelector('[data-header]');
    if (el && !el.dataset.headerBound) {
      el.dataset.headerBound = '1';
      initHeader(el);
    }
  } catch (e) {
    console.warn('[aeperion] motion failed; content is unaffected:', e);
  }
}
